import React, { useContext, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { ShopCart } from 'shop/components/ShopCart'
import { LoginModal } from 'shop/components/Login'
import { FulfillmentFormModalProps } from 'shop/components/FulfillmentFormModal'
import FulfillmentFormModalNew from 'shop/components/FulfillmentFormModalNew'
import {
  InvalidTimeslotPrepTimeWarning,
  InvalidTimeslotWarning
} from 'shop/types/cart'
import CartSwitchModal, {
  CartSwitchModalProps
} from 'shop/components/Modal/CartSwitchModal'
import { TimeSlotModalWrapper } from 'shop/components/Checkout/TimeSlotModal'
import UpsellModal from 'shop/components/Modal/UpsellModal'

export type ModalState = {
  isModalOpen: boolean
  setIsModalOpen: React.Dispatch<any>
}

type ModalKey =
  | 'cart'
  | 'checkout'
  | 'login'
  | 'timeslot'
  | 'fulfillment'
  | 'cartSwitch'
  | 'upsell'

type HookState = {
  [modalName in ModalKey]: ModalState
}

export interface LoginModalProps {
  onClose?: () => void
  inModal?: boolean
  isOverlay?: boolean
  isOpen?: boolean
  inFulfillmentModal?: boolean
}

const modalInitialState = {
  isModalOpen: false,
  setIsModalOpen: () => {}
}

const hookInitialState: HookState = {
  cart: modalInitialState,
  checkout: modalInitialState,
  login: modalInitialState,
  timeslot: modalInitialState,
  fulfillment: modalInitialState,
  cartSwitch: modalInitialState,
  upsell: modalInitialState
}

export const ModalManagerContext =
  React.createContext<HookState>(hookInitialState)

type Hook = () => {
  isModalOpen: (selectedModal?: string) => boolean
  openModal: (selectedModal?: string) => void
  closeModal: (selectModal?: string) => void
  portalModal: (selectedModal?: string) => React.ReactPortal
  loginModal: (props: LoginModalProps) => React.ReactPortal
  timeSlotModal: (
    props: InvalidTimeslotWarning | InvalidTimeslotPrepTimeWarning
  ) => React.ReactPortal
  fulfillmentModal: (props: FulfillmentFormModalProps) => React.ReactPortal
  cartSwitchModal: (props: CartSwitchModalProps) => React.ReactPortal
}

export const useModal: Hook = () => {
  const managedModals = useContext(ModalManagerContext)

  const cartPortal = createPortal(
    <ShopCart closeCartModal={() => closeModal('cart')} isModal={true} />,
    document.body
  )
  const upsellPortal = createPortal(
    <UpsellModal {...{ closeUpsellModal: () => closeModal('upsell') }} />,
    document.body
  )

  const loginModal = ({
    onClose = () => closeModal('login'),
    inModal = false,
    isOverlay = false,
    isOpen = false,
    inFulfillmentModal = false
  }: LoginModalProps) => {
    const loginPortal = createPortal(
      <LoginModal
        {...{
          onClose: onClose,
          inModal,
          isOverlay,
          isOpen,
          inFulfillmentModal
        }}
      />,
      document.body
    )
    return loginPortal
  }

  const timeSlotModal = (
    timeSlotValidation: InvalidTimeslotWarning | InvalidTimeslotPrepTimeWarning
  ) => {
    return createPortal(
      <TimeSlotModalWrapper timeSlotValidation={timeSlotValidation} />,
      document.body
    )
  }

  const fulfillmentModal = (props: FulfillmentFormModalProps) => {
    return createPortal(
      <FulfillmentFormModalNew
        {...props}
        onClose={() => {
          closeModal('fulfillment')
          props.onClose && props.onClose()
        }}
      />,
      document.body
    )
  }

  const cartSwitchModal = (props: CartSwitchModalProps) => {
    return createPortal(<CartSwitchModal {...props} />, document.body)
  }

  const portalModal = useCallback(
    (selectedModal = 'cart') => {
      const portals = { cart: cartPortal, upsell: upsellPortal }

      return portals[selectedModal]
    },
    [cartPortal, upsellPortal]
  )

  const isModalOpen = useCallback(
    (selectedModal = 'cart'): boolean => {
      return managedModals[selectedModal].isModalOpen
    },
    [managedModals]
  )

  const openModal = useCallback(
    (selectedModal = 'cart') => {
      document.body.style.overflow = 'hidden'
      managedModals[selectedModal].setIsModalOpen(true)
    },
    [managedModals]
  )

  const closeModal = useCallback(
    (selectedModal = 'cart') => {
      managedModals[selectedModal].setIsModalOpen(false)

      // fulfillmentModal handles own scroll locking/unlocking
      if (selectedModal === 'fulfillment') return
      // upsellModal handles own scroll locking/unlocking
      if (selectedModal === 'upsell') return
      document.body.style.overflow = ''
    },
    [managedModals]
  )

  return {
    isModalOpen,
    openModal,
    closeModal,
    portalModal,
    loginModal,
    timeSlotModal,
    fulfillmentModal,
    cartSwitchModal
  }
}

export const SetupModal = () => {
  const [isCartModalOpen, setIsCartModalOpen] = useState<boolean>(false)
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState<boolean>(false)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false)
  const [isTimeSlotModalOpen, setIsTimeSlotModalOpen] = useState<boolean>(false)
  const [isFulfillmentModalOpen, setIsFulfillmentModalOpen] =
    useState<boolean>(false)
  const [isCartSwitchModalOpen, setIsCartSwitchModalOpen] =
    useState<boolean>(false)
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState<boolean>(false)

  return {
    cart: {
      isModalOpen: isCartModalOpen,
      setIsModalOpen: setIsCartModalOpen
    },
    checkout: {
      isModalOpen: isCheckoutModalOpen,
      setIsModalOpen: setIsCheckoutModalOpen
    },
    login: {
      isModalOpen: isLoginModalOpen,
      setIsModalOpen: setIsLoginModalOpen
    },
    timeslot: {
      isModalOpen: isTimeSlotModalOpen,
      setIsModalOpen: setIsTimeSlotModalOpen
    },
    fulfillment: {
      isModalOpen: isFulfillmentModalOpen,
      setIsModalOpen: setIsFulfillmentModalOpen
    },
    cartSwitch: {
      isModalOpen: isCartSwitchModalOpen,
      setIsModalOpen: setIsCartSwitchModalOpen
    },
    upsell: {
      isModalOpen: isUpsellModalOpen,
      setIsModalOpen: setIsUpsellModalOpen
    }
  }
}
